"use client"

import React from "react"
import { UseFormReturn } from "react-hook-form"
import { Card, CardContent } from "@/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabelAsterisk } from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { CurrencyInput } from "@/components/ui/currency-input"
import { PercentInput } from "@/components/ui/percent-input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useFormatters } from "@/hooks/use-formatters"
import { useOrganizations } from "@/hooks/use-organizations"
import { type RevenueRecognitionFormData } from "@/lib/schemas"
import { type RevenueRecognitionSchedule } from "@/types/ui/revenue-recognition"
import { cn } from "@/lib/utils"
import { useRevenueRecognitionCalculator } from "@/hooks/use-revenue-recognition-calculator"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Lock, LockOpen } from "lucide-react"
import { REVENUE_RECOGNITION_METHOD, DEFAULT_REVENUE_RECOGNITION_METHOD, type RevenueRecognitionMethod } from "@/types/ui/revenue-recognition"
import type { OrganizationSummary } from "@/types/api/engagement"

interface RevenueRecognitionSectionProps {
  form: UseFormReturn<RevenueRecognitionFormData>
  totalContractValue: number
  engagementStartDate: Date | null
  engagementEndDate: Date | null
  engagementName?: string | null
  selectedClient: number
  currency?: string
  isReadonly?: boolean
  canLockRevenueRecognition?: boolean
  /** Server-resolved org names — fallback for the client name when the RLS-gated list is empty. */
  organizations?: Record<number, OrganizationSummary>
}

export const RevenueRecognitionSection: React.FC<RevenueRecognitionSectionProps> = ({
  form,
  totalContractValue,
  engagementStartDate,
  engagementEndDate,
  engagementName,
  selectedClient,
  currency,
  isReadonly = false,
  canLockRevenueRecognition = false,
  organizations,
}) => {
  const { formatCurrency, formatDate, formatPercent } = useFormatters()
  const { calculateSchedule, percentToAmount, amountToPercent } = useRevenueRecognitionCalculator()
  const { clientOrganizations } = useOrganizations()
  
  // Look up client name from the selected client ID, falling back to the server-resolved org map
  // when the RLS-gated org list is empty for a restricted caller.
  const clientName = React.useMemo(() =>
    clientOrganizations.find(org => org.Id === selectedClient)?.DisplayName
      || organizations?.[selectedClient]?.displayName,
    [clientOrganizations, selectedClient, organizations]
  )

  // Watch form values
  const periods = form.watch('periods')
  
  // Calculation method is UI-only state
  const [calculationMethod, setCalculationMethod] = React.useState<RevenueRecognitionMethod>(DEFAULT_REVENUE_RECOGNITION_METHOD)

  // Get lock status for a period from form data
  const isLocked = React.useCallback((periodId: string) => {
    return periods[periodId]?.isLocked ?? false
  }, [periods])

  // Toggle lock state for a period (persists to form data)
  const togglePeriodLock = React.useCallback((periodId: string) => {
    const currentIsLocked = periods[periodId]?.isLocked ?? false
    const isLocking = !currentIsLocked
    
    // When locking an empty period, default to 0
    if (isLocking && (periods[periodId]?.amount == null)) {
      form.setValue(`periods.${periodId}.amount`, 0)
    }
    
    form.setValue(`periods.${periodId}.isLocked`, isLocking)
  }, [form, periods])

  // Calculate schedule (system amounts only - final amounts come from form)
  const schedule = React.useMemo<RevenueRecognitionSchedule | null>(() => {
    if (!engagementStartDate || !engagementEndDate || totalContractValue <= 0) {
      return null
    }
    
    return calculateSchedule(
      totalContractValue,
      engagementStartDate,
      engagementEndDate,
      calculationMethod
    )
  }, [totalContractValue, engagementStartDate, engagementEndDate, calculationMethod, calculateSchedule])

  // Automatically clean up orphaned periods when schedule changes
  React.useEffect(() => {
    if (schedule) {
      const validPeriodIds = new Set(schedule.periods.map(p => p.id))
      const currentPeriods = form.getValues('periods')
      
      // Build new periods object without orphaned entries
      const cleanedPeriods: Record<string, { amount: number | null; isLocked: boolean }> = {}
      Object.entries(currentPeriods).forEach(([periodId, periodData]) => {
        if (validPeriodIds.has(periodId)) {
          cleanedPeriods[periodId] = periodData
        }
      })
      
      // Only update if something changed
      if (Object.keys(cleanedPeriods).length !== Object.keys(currentPeriods).length) {
        form.setValue('periods', cleanedPeriods)
      }
    }
  }, [schedule, form])

  // Calculate totals from form data (no memoization - must react to form changes)
  const totalRecognizedAmount = Object.values(periods).reduce((sum, p) => sum + (p?.amount ?? 0), 0)
  const totalRecognizedPercentage = amountToPercent(totalRecognizedAmount, totalContractValue) ?? 0
  const difference = totalContractValue - totalRecognizedAmount

  // Check if totals match (useful for validation/styling)
  const totalsMatch = difference === 0
  const differenceColorClass = totalsMatch ? "text-green-700" : "text-yellow-700"

  // Check if any values have been entered
  const hasValues = Object.values(periods).some(v => v?.amount !== null && v?.amount !== undefined)

  // Handler for when percentage field changes - calculates and updates the dollar amount
  const handlePercentageChange = React.useCallback((periodId: string, percentage: number | null) => {
    const amount = percentToAmount(percentage, totalContractValue)
    form.setValue(`periods.${periodId}.amount`, amount)
  }, [form, percentToAmount, totalContractValue])

  const handleAutoCalculate = () => {
    if (!engagementStartDate || !engagementEndDate || totalContractValue <= 0) return
    
    // Build map of locked period amounts
    const lockedPeriodAmounts: Record<string, number> = {}
    Object.entries(periods).forEach(([periodId, periodData]) => {
      if (periodData?.isLocked) {
        lockedPeriodAmounts[periodId] = periodData.amount ?? 0
      }
    })
    
    // Recalculate with locked amounts taken into account
    const recalculated = calculateSchedule(
      totalContractValue,
      engagementStartDate,
      engagementEndDate,
      calculationMethod,
      Object.keys(lockedPeriodAmounts).length > 0 ? lockedPeriodAmounts : undefined
    )
    
    // Apply calculated amounts to unlocked periods
    recalculated.periods.forEach(period => {
      if (!isLocked(period.id)) {
        form.setValue(`periods.${period.id}.amount`, period.systemAmount)
      }
    })
  }

  const handleClear = () => {
    if (schedule) {
      // Only clear unlocked periods
      schedule.periods.forEach(period => {
        if (!isLocked(period.id)) {
          form.setValue(`periods.${period.id}.amount`, null)
        }
      })
    }
  }

  const hasRequiredData = engagementStartDate && engagementEndDate && totalContractValue > 0

  return (
    <div className="flex gap-4">
      {/* Left Panel - Project Details */}
      <Card className="bg-muted/50 rounded-xl shrink-0 w-64">
        <CardContent className="p-6 space-y-6">
          <div className="space-y-0.5">
            <p className="text-sm text-muted-foreground">Project name</p>
            <p className="font-normal">{engagementName || "-"}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-sm text-muted-foreground">Client name</p>
            <p className="font-normal">{clientName || "-"}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-sm text-muted-foreground">Project Period</p>
            <p className="font-normal">
              {engagementStartDate && engagementEndDate
                ? `${formatDate(engagementStartDate)} - ${formatDate(engagementEndDate)}`
                : "-"}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-sm text-muted-foreground">Total contract value</p>
            <p className="font-normal">{formatCurrency(totalContractValue, currency)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Right Panel - Revenue Schedule */}
      <Card className="flex-1 rounded-xl">
        <CardContent className="p-6 flex flex-col gap-6">
          {/* Header */}
          <div className="space-y-0.5">
            <h3 className="text-xl font-semibold">Revenue recognition schedule</h3>
            <p className="text-sm text-muted-foreground">
              Configure how revenue is recognized over the project timeline
            </p>
          </div>

          {!hasRequiredData && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Please complete the following to generate the revenue recognition schedule:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 space-y-1">
                {!engagementStartDate && <li>Start date</li>}
                {!engagementEndDate && <li>End date</li>}
                {totalContractValue <= 0 && <li>Add at least one milestone with an amount greater than 0</li>}
              </ul>
            </div>
          )}

          {hasRequiredData && schedule && (
            <Form {...form}>
              <div className="flex-1 flex flex-col gap-6">
                {/* Revenue Recognition Table */}
                <div className="border rounded-xl overflow-hidden">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="w-14 text-center">
                          <Lock className="h-4 w-4 inline-block" />
                        </TableHead>
                        <TableHead className="text-center w-40">Period</TableHead>
                        <TableHead className="text-right pr-8">
                          Final recognition %
                          <FormLabelAsterisk show={Boolean(form.formState.errors.periods)} />
                        </TableHead>
                        <TableHead className="text-right pr-8">
                          Final recognition $
                          <FormLabelAsterisk show={Boolean(form.formState.errors.periods)} />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedule.periods.map((period, index) => {
                        const periodIsLocked = isLocked(period.id)
                        const isRowDisabled = isReadonly || periodIsLocked
                        
                        return (
                          <TableRow key={period.id} className={index === schedule.periods.length - 1 ? "border-0" : ""}>
                            <TableCell className="text-center">
                              <Switch
                                checked={periodIsLocked}
                                onCheckedChange={() => togglePeriodLock(period.id)}
                                disabled={isReadonly || !canLockRevenueRecognition}
                                aria-label={periodIsLocked ? `Unlock ${period.periodLabel}` : `Lock ${period.periodLabel}`}
                                thumbChildren={
                                  periodIsLocked ? (
                                    <Lock className="h-3 w-3 text-foreground" />
                                  ) : (
                                    <LockOpen className="h-3 w-3 text-foreground" />
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className="text-center">{period.periodLabel}</TableCell>
                            <TableCell>
                              <div className="px-4">
                                <PercentInput
                                  value={amountToPercent(periods[period.id]?.amount, totalContractValue)}
                                  onChange={(percentage) => handlePercentageChange(period.id, percentage)}
                                  className="text-right"
                                  readOnly={isRowDisabled}
                                  aria-label="Final Recognition Percentage"
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="px-4">
                                <FormField
                                  control={form.control}
                                  name={`periods.${period.id}.amount`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <CurrencyInput
                                          {...field}
                                          currency={currency}
                                          className="text-right"
                                          readOnly={isRowDisabled}
                                          aria-label="Final Recognition Amount"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals Section */}
                <div className="bg-muted/50 rounded-xl px-4 py-2">
                  <div className="flex items-center py-2">
                    <div className="flex-1 text-right text-sm text-muted-foreground pr-4">Total recognition</div>
                    <div className="w-36 pr-4 font-semibold text-right">{formatPercent(totalRecognizedPercentage)}</div>
                    <div className="w-36 pr-4 font-semibold text-right">{formatCurrency(totalRecognizedAmount, currency)}</div>
                  </div>
                  <div className="flex items-center py-2">
                    <div className={cn("flex-1 text-right text-sm pr-4", differenceColorClass)}>
                      Difference (must be $0 to post):
                    </div>
                    <div className={cn("w-36 pr-4 font-semibold text-right", differenceColorClass)}>
                      {formatPercent(100 - totalRecognizedPercentage)}
                    </div>
                    <div className={cn("w-36 pr-4 font-semibold text-right", differenceColorClass)}>
                      {formatCurrency(difference, currency)}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center gap-2">
                  {/* Left side - Calculation Method */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Calculation Method:</span>
                    <Select 
                      onValueChange={(value) => setCalculationMethod(value as RevenueRecognitionMethod)} 
                      value={calculationMethod}
                      disabled={isReadonly}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={REVENUE_RECOGNITION_METHOD.STRAIGHT_LINE}>
                          Straight-Line
                        </SelectItem>
                        <SelectItem value={REVENUE_RECOGNITION_METHOD.WEIGHTED}>
                          Monthly Weighted
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Right side - Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleClear}
                      disabled={isReadonly || !hasValues}
                    >
                      Clear
                    </Button>
                    <Button
                      variant={totalsMatch ? "outline" : "default"}
                      onClick={handleAutoCalculate}
                      disabled={isReadonly}
                    >
                      {hasValues ? 'Re-calculate' : 'Calculate'}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

